import { useState } from "react";
import type { ReactNode } from "react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import * as Tooltip from "@radix-ui/react-tooltip";
import { History, Info } from "lucide-react";
import type { RecordData } from "../core";
import { useLocale } from "../react/providers/LocaleProvider";
import { useBootstrap } from "../react/queries/useBoot";
import { useCommand } from "../react/hooks/useCommand";
import { command } from "../core";
import { fmtDateTime } from "../widgets/builtins";
import { isNeverEdited, uidLabel } from "./quickMetaHelpers";

// Quick Meta View (Enhancement 05) — Radix DropdownMenu surface matching the
// OrgSwitcher's chrome. Header row carries the uppercase "METADATA" label on
// the left and a small History icon on the right — that's the "View full
// history" affordance, disabled with a tooltip until foundation.audit ships.
// Body is a 4-row key/value grid (created on / by, last modified on / by).

interface QuickMetaPopoverProps {
    record: RecordData | null | undefined;
    isNew: boolean;
    modelKey: string;
    recordUuid: string;
}

export function QuickMetaPopover({
    record,
    isNew,
    modelKey,
    recordUuid,
}: QuickMetaPopoverProps): ReactNode {
    const locale = useLocale();
    const boot = useBootstrap();
    const dispatch = useCommand();
    const [open, setOpen] = useState(false);

    if (isNew || !record) return null;

    const createdAt = record.created_at_utc;
    const updatedAt = record.updated_at_utc;
    const createdBy = uidLabel(record.created_uid);
    const updatedBy = uidLabel(record.updated_uid);

    const neverEdited = isNeverEdited(createdAt, updatedAt);
    const createdAtText = typeof createdAt === "string" ? fmtDateTime(createdAt, locale.timezone) : "—";
    const updatedAtText = neverEdited
        ? "Never edited"
        : typeof updatedAt === "string"
            ? fmtDateTime(updatedAt, locale.timezone)
            : "—";

    const auditAction = boot.data ? findActionByKey(boot.data.menus, "audit-history") : null;
    const auditAvailable = Boolean(auditAction);

    const openAuditHistory = () => {
        if (!auditAvailable || !auditAction) return;
        setOpen(false);
        void dispatch(command("OPEN_ACTION", { actionKey: auditAction }));
    };

    return (
        <DropdownMenu.Root open={open} onOpenChange={setOpen}>
            <DropdownMenu.Trigger asChild>
                <button
                    type="button"
                    className="quick-meta__trigger"
                    aria-label="Record metadata"
                    title="Record metadata"
                >
                    <Info size={14} strokeWidth={2} />
                </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    className="rdx-menu quick-meta"
                    align="start"
                    sideOffset={6}
                    onCloseAutoFocus={(e) => e.preventDefault()}
                >
                    <div className="quick-meta__head">
                        <DropdownMenu.Label className="rdx-menu__label quick-meta__title">
                            Metadata
                        </DropdownMenu.Label>
                        {auditAvailable ? (
                            <button
                                type="button"
                                className="quick-meta__history"
                                onClick={openAuditHistory}
                                aria-label="View full history"
                                title="View full history"
                            >
                                <History size={14} strokeWidth={2} />
                            </button>
                        ) : (
                            <Tooltip.Provider delayDuration={300}>
                                <Tooltip.Root>
                                    <Tooltip.Trigger asChild>
                                        <span
                                            className="quick-meta__history is-disabled"
                                            aria-disabled="true"
                                            aria-label="View full history (audit module not yet available)"
                                            data-model-key={modelKey}
                                            data-record-uuid={recordUuid}
                                        >
                                            <History size={14} strokeWidth={2} />
                                        </span>
                                    </Tooltip.Trigger>
                                    <Tooltip.Portal>
                                        <Tooltip.Content className="quick-meta__tooltip" sideOffset={4}>
                                            Audit module not yet available
                                        </Tooltip.Content>
                                    </Tooltip.Portal>
                                </Tooltip.Root>
                            </Tooltip.Provider>
                        )}
                    </div>
                    <div className="quick-meta__body">
                        <section className="quick-meta__section">
                            <h4 className="quick-meta__section-label">Created</h4>
                            <div className="quick-meta__section-value">{createdAtText}</div>
                            <div className="quick-meta__section-meta">by {createdBy}</div>
                        </section>
                        <section className="quick-meta__section">
                            <h4 className="quick-meta__section-label">Last modified</h4>
                            {neverEdited ? (
                                <div className="quick-meta__section-value quick-meta__section-value--muted">Never edited</div>
                            ) : (
                                <>
                                    <div className="quick-meta__section-value">{updatedAtText}</div>
                                    <div className="quick-meta__section-meta">by {updatedBy}</div>
                                </>
                            )}
                        </section>
                        <section className="quick-meta__section">
                            <h4 className="quick-meta__section-label">Record UID</h4>
                            <div className="quick-meta__section-value quick-meta__uuid">{recordUuid}</div>
                        </section>
                    </div>
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
}

// Walks `boot.menus` looking for the first menu entry whose action.key
// matches. Parallels `findActionForModel` but indexes by action key.
function findActionByKey(
    menus: Record<string, import("../core").MenuEntry[]> | undefined,
    actionKey: string,
): string | null {
    if (!menus) return null;
    for (const entries of Object.values(menus)) {
        const found = walk(entries, actionKey);
        if (found) return found;
    }
    return null;
}

function walk(entries: import("../core").MenuEntry[], actionKey: string): string | null {
    for (const entry of entries) {
        if (entry.action?.key === actionKey) return entry.action.key;
        if (entry.children && entry.children.length > 0) {
            const found = walk(entry.children, actionKey);
            if (found) return found;
        }
    }
    return null;
}
